import { getCallCenterCandidatesForManager } from '@/API/repositories/callCenterCandidate';
import { getCallCenterPositions } from '@/API/repositories/callCenterPosition';
import { useRequestsContext } from '@/common/hooks/requestHook';
import { useMessageQueueContext } from '@/common/hooks/useMessageQueue';
import { CALL_CENTER_POSITION_OPTIONS } from '@/pages/admin/callCenterPosition/constants/callCenterPositions';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const useRecruitmentManager = () => {
	const [showCandidateForm, setShowCandidateForm] = useState(false);
	const [callCenterPositionsOptions, setCallCenterPositionsOptions] = useState(
		[],
	);
	const [callCenterCandidates, setCallCenterCandidates] = useState([]);
	const [visibleCandidates, setVisibleCandidates] = useState([]);
	const [searchParams, setSearchParams] = useSearchParams();

	const { makeRequest, hasUnfilledRequest } = useRequestsContext();
	const { addMessage } = useMessageQueueContext();

	const loadBaseData = async () => {
		await Promise.all([loadCallCenterPositions(), loadCandidates()]);
	};

	const loadCandidates = async () => {
		const response = await makeRequest(getCallCenterCandidatesForManager);

		if (!response?.data) {
			return addMessage("Couldn't load call center candidates", 'error');
		}

		setCallCenterCandidates(response.data);
	};

	const selectBySearchParams = () => {
		if (searchParams.has('candidate')) {
			const candidate = callCenterCandidates.find(
				(c) => c._id === searchParams.get('candidate'),
			);

			if (candidate) {
				setShowCandidateForm(candidate);
				setSearchParams('');
			}
		}
	};

	const loadCallCenterPositions = async () => {
		const response = await makeRequest(getCallCenterPositions);

		if (!response?.data) {
			return addMessage("Couldn't load call center positions", 'error');
		}

		setCallCenterPositionsOptions(
			response.data.map(({ title, _id, market }) => ({
				label: CALL_CENTER_POSITION_OPTIONS.find((o) => o.value === title)
					?.label,
				value: _id,
				market: market,
			})),
		);
	};

	useEffect(() => {
		loadBaseData();
	}, []);

	useEffect(() => {
		selectBySearchParams();
	}, [searchParams, callCenterCandidates]);

	const isLoading = hasUnfilledRequest(
		getCallCenterCandidatesForManager,
		getCallCenterPositions,
	);

	return {
		hasUnfilledRequest,
		callCenterCandidates,
		setVisibleCandidates,
		setShowCandidateForm,
		visibleCandidates,
		showCandidateForm,
		callCenterPositionsOptions,
		loadCandidates,
		isLoading,
	};
};

export default useRecruitmentManager;
